import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { TFunction } from 'i18next';
import { describeError } from '../errors.js';
import { type AdminHttpClient, withUnauthorizedRetry } from '../http.js';
import {
  createTenantContextIntentCoordinator,
  tenantContextRenewalDelayMs,
  type TenantContextIntent,
} from '../tenant-context.js';
import type { AdminSession, Tenant, ToastMessage } from '../types.js';
import type { AdminSessionLifecycle } from '../session-lifecycle.js';

interface UseTenantContextOptions {
  session: AdminSession | undefined;
  sessionRef: MutableRefObject<AdminSession | undefined>;
  lifecycle: AdminSessionLifecycle;
  platformHttp: AdminHttpClient;
  tenantHttp: AdminHttpClient;
  persistSession: (next: AdminSession | undefined) => void;
  showToast: (toast: ToastMessage) => void;
  t: TFunction;
}

export function useTenantContext({
  session,
  sessionRef,
  lifecycle,
  platformHttp,
  tenantHttp,
  persistSession,
  showToast,
  t,
}: UseTenantContextOptions) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const requestCoordinatorRef = useRef(createTenantContextIntentCoordinator());
  const roles = session?.bootstrap.membership.roles ?? [];
  const isPlatformAdmin = roles.includes('platform_admin');
  const isPartnerAdmin = roles.includes('partner_admin');
  const isCrossTenantOperator = isPlatformAdmin || isPartnerAdmin;
  const selectedTenantId = session?.tenantContext?.tenantId;
  const tenantContextExpiresAt = session?.tenantContext?.expiresAt;
  const activeTenantId = isCrossTenantOperator ? selectedTenantId : session?.bootstrap.tenant.id;
  const hasSession = Boolean(session);

  useEffect(() => {
    if (!hasSession || !isCrossTenantOperator) {
      setTenants([]);
      return;
    }
    let current = true;
    void (isPartnerAdmin ? platformHttp.partnerListTenants() : platformHttp.getTenants())
      .then(items => { if (current) setTenants(items); })
      .catch((error) => { if (current) showToast({ tone: 'error', message: describeError(error, t) }); });
    return () => { current = false; };
  }, [
    hasSession,
    isCrossTenantOperator,
    isPartnerAdmin,
    platformHttp,
    session?.accessToken,
    showToast,
    t,
  ]);

  const selectTenantContext = useCallback(
    async (
      tenantId: string,
      opts?: { silent?: boolean; intent?: TenantContextIntent }
    ): Promise<boolean> => {
      const coordinator = requestCoordinatorRef.current;
      const request = coordinator.begin(opts?.intent ?? 'manual');
      if (!request) return false;
      const requestAccessToken = sessionRef.current?.accessToken;
      const requestGeneration = lifecycle.generation;
      const isRequestCurrent = () =>
        coordinator.isCurrent(request) &&
        requestAccessToken !== undefined &&
        lifecycle.generation === requestGeneration;
      try {
        const current = sessionRef.current;
        if (!current || current.accessToken !== requestAccessToken) return false;
        if ((opts?.intent ?? 'manual') === 'manual') {
          const withoutContext = { ...current }; delete withoutContext.tenantContext;
          persistSession(withoutContext);
        }
        if (!tenantId) {
          if (!isRequestCurrent()) return false;
          const next = { ...current }; delete next.tenantContext;
          persistSession(next);
          return true;
        }
        const actAs = isPartnerAdmin
          ? await platformHttp.partnerActAsTenant(tenantId)
          : await platformHttp.actAsTenant(tenantId);
        let tenant = tenants.find((item) => item.id === tenantId);
        if (!tenant) {
          const refreshed = isPartnerAdmin
            ? await platformHttp.partnerListTenants()
            : await platformHttp.getTenants();
          if (!isRequestCurrent()) return false;
          setTenants(refreshed);
          tenant = refreshed.find((item) => item.id === tenantId);
        }
        if (!isRequestCurrent()) return false;
        const latest = sessionRef.current;
        if (!latest) return false;
        persistSession({
          ...latest,
          tenantContext: {
            tenantId,
            tenantName: tenant?.name ?? tenantId,
            accessToken: actAs.accessToken,
            expiresAt: actAs.expiresAt,
            roles: actAs.roles,
          },
        });
        if (!opts?.silent) {
          showToast({
            tone: 'success',
            message: t('appShell.tenantContextChanged', { name: tenant?.name ?? tenantId }),
          });
        }
        return true;
      } catch (error) {
        if (!isRequestCurrent()) return false;
        showToast({ tone: 'error', message: describeError(error, t) });
        return false;
      } finally {
        coordinator.finish(request);
      }
    },
    [isPartnerAdmin, persistSession, platformHttp, sessionRef, showToast, t, tenants, lifecycle]
  );

  const renewTenantContext = useCallback(async () => {
    const tenantId = sessionRef.current?.tenantContext?.tenantId;
    if (!tenantId) throw new Error('tenant_context_required');
    const renewed = await selectTenantContext(tenantId, { silent: true, intent: 'renewal' });
    if (!renewed) throw new Error('tenant_context_renew_failed');
  }, [selectTenantContext, sessionRef]);

  const tenantHttpWithRenewal = useMemo(
    () => withUnauthorizedRetry(tenantHttp, renewTenantContext),
    [renewTenantContext, tenantHttp]
  );
  const tenantScopedHttp = isCrossTenantOperator ? tenantHttpWithRenewal : platformHttp;

  useEffect(() => {
    if (!isCrossTenantOperator || !selectedTenantId || !tenantContextExpiresAt) return;
    const timer = window.setTimeout(
      () => void selectTenantContext(selectedTenantId, { silent: true, intent: 'renewal' }),
      tenantContextRenewalDelayMs(tenantContextExpiresAt)
    );
    return () => window.clearTimeout(timer);
  }, [isCrossTenantOperator, selectedTenantId, selectTenantContext, tenantContextExpiresAt]);

  return {
    tenants,
    selectedTenantId,
    activeTenantId,
    isPlatformAdmin,
    isPartnerAdmin,
    isCrossTenantOperator,
    tenantScopedHttp,
    selectTenantContext,
  };
}
